import {
	Button,
	Checkbox,
	FormControlLabel,
	MenuItem,
	TextField,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from '../../../../_metronic/_partials/controls'
import { getContractById, updateContract } from '../../../../api/contract'
import { alertError, alertSuccess } from '../../../../utils/logger'

const euros = (centimos) =>
	new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(
		(centimos ?? 0) / 100
	)

const dia = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '')

const ESTADOS = [
	['pending', 'pendiente · firmado y sin empezar'],
	['active', 'activo'],
	['unpaid', 'impagado · recibo devuelto'],
	['ended', 'terminado'],
]

/**
 * Editar un contrato.
 *
 * Dos campos NO se tocan a mano y por razones distintas:
 *
 *  · la REFERENCIA es lo único que impide que un alta repetida duplique el
 *    club y le entregue otros veinticinco aparatos;
 *  · la CUOTA está congelada al firmar. Solo se recalcula si cambian los
 *    aparatos, y eso lo hace el servidor con el catálogo. Si se pudiera
 *    escribir aquí, un descuido serían meses cobrando de más o de menos.
 */
export default function EditContractsPage() {
	const { id } = useParams()
	const [contrato, setContrato] = useState(null)
	const [aparatosOriginales, setAparatosOriginales] = useState(null)
	const [trabajando, setTrabajando] = useState(false)
	const history = useHistory()

	useEffect(() => {
		getContractById(id)
			.then((res) => {
				setContrato(res.data)
				setAparatosOriginales(res.data.devices)
			})
			.catch((error) => {
				alertError({ error, customMessage: 'No se pudo cargar el contrato.' })
				history.push('/contracts')
			})
	}, [id, history])

	if (!contrato)
		return null

	const cambiar = (campo) => (e) => {
		const valor = e.target.type === 'checkbox' ? e.target.checked : e.target.value
		setContrato((c) => ({ ...c, [campo]: valor }))
	}

	const cambiarBilling = (campo) => (e) =>
		setContrato((c) => ({ ...c, billing: { ...c.billing, [campo]: e.target.value } }))

	const aparatosCambiados =
		Number(contrato.devices) !== Number(aparatosOriginales)

	function guardar() {
		setTrabajando(true)
		updateContract(id, {
			devices: Number(contrato.devices),
			pilot: contrato.pilot,
			season: contrato.season,
			startDate: contrato.startDate,
			commitmentMonths: Number(contrato.commitmentMonths),
			status: contrato.status,
			billing: contrato.billing,
			notes: contrato.notes,
		})
			.then((res) => {
				setContrato(res.data)
				setAparatosOriginales(res.data.devices)
				alertSuccess({
					title: 'Guardado',
					customMessage: res.data.precioRecalculado
						? `Cuota recalculada: ${euros(res.data.monthlyCents)}/mes`
						: 'Contrato actualizado.',
				})
			})
			.catch((error) => alertError({ error, customMessage: 'No se pudo guardar.' }))
			.finally(() => setTrabajando(false))
	}

	return (
		<Card>
			<CardHeader title={`Contrato de ${contrato.club?.fullName ?? 'sin club'}`}>
				<CardHeaderToolbar>
					<button
						type="button"
						className="btn btn-light"
						onClick={() => history.push('/contracts')}
					>
						Volver
					</button>
				</CardHeaderToolbar>
			</CardHeader>
			<CardBody>
				{/* Lo que no se toca, junto y a la vista para que se sepa por qué. */}
				<div
					style={{
						background: '#f4f7fa',
						borderRadius: 8,
						padding: 16,
						marginBottom: 24,
					}}
				>
					<div>
						Cuota:{' '}
						<strong>
							{contrato.pilot ? '—' : `${euros(contrato.monthlyCents)}/mes + IVA`}
						</strong>{' '}
						<span style={{ color: '#7b8794' }}>
							· congelada al firmar; solo cambia si cambian los aparatos
						</span>
					</div>
					<div style={{ marginTop: 6 }}>
						Alta: <strong>{euros(contrato.setupFeeCents)}</strong>
					</div>
					<div style={{ marginTop: 6, color: '#7b8794' }}>
						Referencia: <code>{contrato.reference ?? '—'}</code> · no se edita: es lo que evita
						duplicar un alta
					</div>
					{contrato.billing?.stripeSubscriptionId && (
						<div style={{ marginTop: 6, color: '#7b8794' }}>
							Stripe: <code>{contrato.billing.stripeSubscriptionId}</code>
						</div>
					)}
				</div>

				<TextField
					label="Aparatos"
					type="number"
					value={contrato.devices}
					onChange={cambiar('devices')}
					margin="normal"
					fullWidth
					helperText={
						aparatosCambiados
							? 'Al guardar, el servidor recalcula la cuota con el catálogo'
							: ' '
					}
				/>

				<TextField
					select
					label="Estado"
					value={contrato.status}
					onChange={cambiar('status')}
					margin="normal"
					fullWidth
					helperText="Marcar impagado NO le quita los aparatos al club: solo lo señala"
				>
					{ESTADOS.map(([valor, texto]) => (
						<MenuItem key={valor} value={valor}>
							{texto}
						</MenuItem>
					))}
				</TextField>

				<TextField
					label="Empieza"
					type="date"
					value={dia(contrato.startDate)}
					onChange={cambiar('startDate')}
					InputLabelProps={{ shrink: true }}
					margin="normal"
					fullWidth
				/>

				<TextField
					label="Meses de compromiso"
					type="number"
					value={contrato.commitmentMonths ?? 0}
					onChange={cambiar('commitmentMonths')}
					margin="normal"
					fullWidth
					helperText={
						contrato.commitmentEndDate
							? `Ahora mismo obliga hasta el ${new Date(
								contrato.commitmentEndDate
							).toLocaleDateString('es-ES')}`
							: ' '
					}
				/>

				<TextField
					select
					label="Cobro"
					value={contrato.billing?.method ?? 'transfer'}
					onChange={cambiarBilling('method')}
					margin="normal"
					fullWidth
				>
					<MenuItem value="transfer">Transferencia</MenuItem>
					<MenuItem value="stripe">Stripe</MenuItem>
				</TextField>

				<TextField
					label="NIF"
					value={contrato.billing?.taxId ?? ''}
					onChange={cambiarBilling('taxId')}
					margin="normal"
					fullWidth
					helperText="Para la factura. No tiene por qué coincidir con el club publicado"
				/>
				<TextField
					label="Razón social"
					value={contrato.billing?.legalName ?? ''}
					onChange={cambiarBilling('legalName')}
					margin="normal"
					fullWidth
				/>

				<TextField
					label="Notas"
					value={contrato.notes ?? ''}
					onChange={cambiar('notes')}
					margin="normal"
					fullWidth
					multiline
				/>

				<FormControlLabel
					control={<Checkbox checked={!!contrato.pilot} onChange={cambiar('pilot')} />}
					label="Piloto: colabora sin pagar y no cuenta como ingreso"
				/>
				<br />
				<FormControlLabel
					control={<Checkbox checked={!!contrato.season} onChange={cambiar('season')} />}
					label="Temporada completa pagada por adelantado"
				/>

				<div style={{ marginTop: 24 }}>
					<Button
						variant="contained"
						color="primary"
						onClick={guardar}
						disabled={trabajando}
					>
						Guardar
					</Button>
				</div>

				{contrato.endDate && (
					<p style={{ marginTop: 16, color: '#7b8794' }}>
						Baja anotada: deja de tener servicio el{' '}
						{new Date(contrato.endDate).toLocaleDateString('es-ES')}. La fecha se calculó al
						recibir el aviso y no se recalcula: es la que se le dijo al club.
					</p>
				)}
			</CardBody>
		</Card>
	)
}
